import type { Placement } from '@floating-ui/react';
import type { TippyProps } from '@tippyjs/react';
import Tippy from '@tippyjs/react';
import { omit, pick } from 'lodash-es';
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { sleep } from '../../async';
import { cx } from '../../cx';
import { Tooltip as ITooltip } from '../../tooltip';
import './index.css';

export interface TooltipProps extends Omit<TippyProps, 'children'> {
  popup?: ReactNode | undefined;
  popupClass?: string;
  equalWidth?: boolean;
  popupClick?: (event: MouseEvent) => void;
  interactive?: boolean;
  maxWidth?: number | string;
  theme?: 'next-tooltip' | 'members-tooltip' | 'next-modal' | 'none';
  lazyLoad?: boolean;
  children?: any;
  style?: React.CSSProperties;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  id?: string;
}

export const Tooltip = (props: TooltipProps) => {
  const {
    popup,
    offset = [0, 10],
    placement = 'top',
    children,
    className,
    duration = [200, 50],
    delay = [200, 50],
    popupClass = '',
    popupClick,
    equalWidth,
    arrow = false,
    onClick,
    style,
    trigger = 'mouseenter',
    interactive = false,
    maxWidth = 200,
    theme = 'next-tooltip',
    animation = 'fade',
    visible,
    appendTo = () => document.body,
    disabled,
    lazyLoad = false,
    onShow,
    onShown,
    onHidden,
    ...rest
  } = props;
  const ref = useRef<HTMLDivElement>(null);

  // @tippyjs/react: Cannot specify `trigger` prop in controlled mode (`visible` prop)
  const visibleProps = typeof visible === 'boolean' ? { visible } : { trigger };

  /** 有一些特殊的类似data-set属性需要从tippy上挪给div。在这里记录 */
  const moveRestAttribute = ['data-no-cancel-selected', 'data-test-id', 'id', 'data-menu-id'];
  const [showContent, setShowContent] = useState(!lazyLoad);
  const disable = disabled || !popup;

  useEffect(() => {
    if (visible !== undefined) {
      if (!visible) {
        void sleep(100).then(() => {
          setShowContent(visible);
        });
      } else {
        setShowContent(visible);
      }
    }
  }, [visible]);

  const content = (
    <div
      data-no-cancel-selected
      ref={ref as any}
      className={cx('flex', className)}
      style={style}
      onClick={onClick}
      {...(pick(rest, moveRestAttribute) as any)}
    >
      {children}
    </div>
  );

  if (disable) {
    return content;
  }
  let open = 200;
  let close = 0;
  if (typeof delay === 'number') {
    open = delay;
  } else if (Array.isArray(delay)) {
    open = delay[0] ?? 200;
    close = delay[1] ?? 0;
  }

  if (!interactive && theme === 'next-tooltip' && trigger === 'mouseenter' && !popupClick) {
    return (
      <ITooltip
        flexCenter={false}
        placement={placement as Placement}
        delay={{
          open,
          close,
        }}
        offset={offset}
        popup={popup}
        className={cx('flex', className)}
        popupClassName={cx('break-words', popupClass)}
        maxWidth={equalWidth ? ref.current?.clientWidth : maxWidth}
        onClick={onClick}
        style={style}
        {...(pick(rest, moveRestAttribute) as any)}
      >
        {children}
      </ITooltip>
    );
  }

  return (
    <Tippy
      onHidden={(instance) => {
        if (lazyLoad) {
          setShowContent(false);
        }
        onHidden?.(instance);
      }}
      onShow={(instance) => {
        if (lazyLoad) {
          setShowContent(true);
        }
        onShow?.(instance);
      }}
      onShown={(instance) => {
        if (lazyLoad) {
          setShowContent(true);
        }
        onShown?.(instance);
      }}
      interactive={interactive}
      arrow={arrow}
      delay={delay}
      duration={duration}
      disabled={disable}
      theme={theme}
      placement={placement}
      offset={offset}
      maxWidth={equalWidth ? ref.current?.clientWidth : maxWidth}
      appendTo={appendTo}
      animation={animation}
      content={
        showContent && (
          <div
            className={cx('text-t4-medium whitespace-pre-line', popupClass)}
            onClick={popupClick}
          >
            {showContent && popup}
          </div>
        )
      }
      {...omit(rest, moveRestAttribute)}
      {...visibleProps}
    >
      {content}
    </Tippy>
  );
};
